import { useState } from 'react';
import { Table, Button, Loader, Center, Select, Badge, Text, Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconUsers, IconBriefcase, IconAlertTriangle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { Specialty } from '../components/Specialty';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { EmptyState } from '../components/EmptyState';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StaffCreateModal } from '../components/StaffCreateModal';
import { useAuth } from '../contexts/AuthContext';
import { canAccess, canManageRole } from '../lib/permissions';
import { FaceIdCell, useFaceLinks } from '../components/FaceIdLink';
import { formatPhone, formatSom } from '../lib/format';

interface Staff { id: string; login: string; fullName: string; role: string; phone?: string; isActive: boolean; specialty?: string | null }

// PARENT/STUDENT bu yerda emas — ota-ona o'quvchi kartochkasidan yaratiladi (kamida 1 farzandga bog'lanadi)
const ROLE_VALUES = ['DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'TEACHER', 'HOMEROOM_TEACHER', 'RECEPTION'];
// Sahifa doirasi: o'qituvchilar alohida sahifada, shu sabab bu yerda faqat boshqaruv rollari.
// Filtr tanlanmasa AYNI shu satr `roles` sifatida ketadi — ya'ni ro'yxat doirasi o'zgarmaydi.
const STAFF_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION'];

export default function Staff() {
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [sort, setSort] = useState('createdAt:desc');
  const [roleF, setRoleF] = useState('');
  const [statusF, setStatusF] = useState('');
  const { t } = useTranslation();
  const face = useFaceLinks();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const STAFF_ROLE_OPTIONS = STAFF_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }));

  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    roles: roleF || STAFF_ROLES.join(','),
    isActive: statusF || undefined,
    q: qDeb || undefined,
    sort,
  };
  const { items: staff, isLoading, fetching, total, pages, page, setPage, limit, setLimit} = usePagedQuery<Staff>('staff-all', '/staff', filtrlar);

  // Joriy oy bo'yicha maosh qoldig'i (qarz) — ro'yxatda "kim to'lanmagan" ni ko'rsatish uchun
  const salaryMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  // Maosh qoldig'i — pul: faqat direktor va buxgalter (`payments` bo'limi)
  const { user } = useAuth();
  const money = canAccess(user?.role, 'payments');
  const { data: paySummary } = useQuery({
    queryKey: ['payroll-summary', salaryMonth],
    enabled: money,
    // `warnings`/`activeYear` — docs/ELEKTRON-JURNAL.md §6.7: PER_LESSON maoshi faol
    // o'quv yili sinflari jadvalidan hisoblanadi, faol yil yoki sinf bo'lmasa QOLDIQ
    // hammada jimgina 0 bo'lib ko'rinardi. Endi sabab ro'yxat tepasida aytiladi.
    queryFn: async () => (await api.get<{
      rows: { userId: string; remaining: number }[];
      warnings?: string[];
      activeYear?: { id: string; name: string } | null;
    }>('/payroll/summary', { params: { month: salaryMonth } })).data,
  });
  const payWarning = paySummary?.warnings?.find((w) => w === 'NO_ACTIVE_YEAR' || w === 'ACTIVE_YEAR_HAS_NO_CLASSES');
  const payMap: Record<string, number> = {};
  paySummary?.rows.forEach((r) => { payMap[r.userId] = r.remaining; });

  return (
    <>
      <PageHeader title={t('pages.staff.title')} subtitle={`${t('common.total')}: ${total}`} action={<Button leftSection={<IconPlus size={18} />} onClick={open}>{t('pages.staff.add')}</Button>} />

      {payWarning && (
        <Alert mb="md" color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title={t('staffPage.payrollWarnTitle')}>
          {payWarning === 'NO_ACTIVE_YEAR'
            ? t('staffDetail.payrollWarnNoYear')
            : t('staffDetail.payrollWarnNoClasses', { name: paySummary?.activeYear?.name ?? '' })}
        </Alert>
      )}

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('staffPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'createdAt:desc', label: t('staffPage.sortNewest') },
          { value: 'fullName:asc', label: t('staffPage.sortNameAsc') },
          { value: 'role:asc', label: t('staffPage.sortRole') },
        ]}
      >
        <Select
          placeholder={t('staffPage.role')}
          data={STAFF_ROLE_OPTIONS}
          value={roleF || null}
          onChange={(v) => setRoleF(v ?? '')}
          clearable
          searchable
          w={180}
        />
        <Select
          placeholder={t('staffPage.status')}
          data={[
            { value: 'true', label: t('staffPage.active') },
            { value: 'false', label: t('block.blocked') },
          ]}
          value={statusF || null}
          onChange={(v) => setStatusF(v ?? '')}
          clearable
          w={150}
        />
        <TableExport<Staff>
          endpoint="/staff"
          params={filtrlar}
          filename={t('nav.staff')}
          ustunlar={[
            { key: 'fullName', label: t('staffPage.fullName') },
            { key: 'login', label: t('staffPage.login') },
            { key: 'role', label: t('staffPage.role'), get: (r) => t(`roles.${r.role}`) },
            { key: 'specialty', label: t('staffPage.specialty') },
            { key: 'phone', label: t('fields.phone') },
            { key: 'isActive', label: t('fields.status'), get: (r) => (r.isActive ? '+' : '-') },
          ]}
        />
      </TableToolbar>
      <SectionCard title={t('common.list')} icon={IconBriefcase} p={0} pos="relative">
        <FetchOverlay visible={fetching} />
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !staff.length ? (
          <EmptyState icon={IconUsers} title={t('staffPage.empty')} description={t('staffPage.emptyDesc')} />
        ) : (
          <Table.ScrollContainer minWidth={face.enabled ? 780 : 640}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('staffPage.fullName')}</Table.Th>
                  <Table.Th>{t('staffPage.login')}</Table.Th>
                  <Table.Th>{t('staffPage.role')}</Table.Th>
                  <Table.Th>{t('staffPage.phone')}</Table.Th>
                  <Table.Th>{t('staffPage.status')}</Table.Th>
                  {money && <Table.Th w={80} ta="center">{t('staffDetail.salary')}</Table.Th>}
                  {face.enabled && <Table.Th>FACE ID</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {staff.map((u) => (
                  <Table.Tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/staff/${u.id}`)}>
                    <Table.Td fw={500}>{u.fullName}<Specialty value={u.specialty} /></Table.Td>
                    <Table.Td><Text ff="monospace" size="sm">{u.login}</Text></Table.Td>
                    <Table.Td><Badge variant="light">{t(`roles.${u.role}`)}</Badge></Table.Td>
                    <Table.Td>{formatPhone(u.phone)}</Table.Td>
                    <Table.Td><Badge color={u.isActive ? 'teal' : 'gray'} variant="dot">{u.isActive ? t('staffPage.active') : t('block.blocked')}</Badge></Table.Td>
                    {money && (
                    <Table.Td ta="center">
                      {payMap[u.id] != null && (
                        payMap[u.id] > 0
                          ? <Badge color="red" variant="light">{formatSom(payMap[u.id])}</Badge>
                          : <Badge color="teal" variant="light">{t('staffDetail.fullyPaid')}</Badge>
                      )}
                    </Table.Td>
                    )}
                    {face.enabled && <Table.Td><FaceIdCell user={u} link={face.map[u.id]} /></Table.Td>}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <StaffCreateModal
        opened={opened}
        onClose={close}
        roles={ROLE_VALUES.filter((r) => canManageRole(user?.role, r))}
        title={t('pages.staff.add')}
      />
    </>
  );
}
